// One persistent 3D world holding every course island in a vertical stack.
// The selected course (0 → COURSES.length-1) drives a single camera that flies
// along the row, so moving between courses is a continuous shot rather than
// three separate canvases fading in and out.
import React, { Suspense, useLayoutEffect, useMemo, useRef } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { Environment, useGLTF } from '@react-three/drei';
import { Bloom, EffectComposer, SMAA } from '@react-three/postprocessing';
import * as THREE from 'three';
import useTheme from '../../utils/useTheme';
import { useSceneBackdrop } from '../SceneBackdrop';
import { COURSES, SPACING } from './courseData';
import GodotIsland from './GodotIsland';
import PythonIsland from './PythonIsland';
import Web3DIsland from './Web3DIsland';
import MinecraftIsland from './MinecraftIsland';
import { PICKAXE_MODEL } from './MinecraftIsland';
import PlaceholderIsland from './PlaceholderIsland';
import { clamp01 } from './helpers';
import {
  REVEAL_SECONDS,
  applySolidReveal,
  getRevealNoiseTexture,
  createRevealState,
  isRevealSurface,
  makeLatticeMaterial,
  makeSolidMaterial,
  setRevealPalette,
} from './hologramReveal';

/**
 * Ask for every course model together when the course world is actually used.
 *
 * Without this they arrive in four sequential waves. React suspends on the
 * *first* unresolved hook in a component, so the hooks after it never run:
 * GodotIsland asks for its platform and stops there, and only once that has
 * landed does it get far enough to ask for the bot — which in turn has to render
 * before anything asks for the rifle it is holding, or the crates beside it.
 * Measured on localhost, where each fetch itself takes 5-8ms, the last request
 * still did not go out until 460ms after the first. On a real connection every
 * one of those waves costs a full round trip instead of a few milliseconds.
 *
 * Preloading puts all of them in flight at once, so the suspensions above
 * resolve from cache in whatever order they happen to be reached. Keeping the
 * request route-scoped matters: module-scope preloads made the home character
 * compete with seven invisible course assets on a visitor's first page load.
 */
const COURSE_MODELS = [
  '/GodotBotPlatform.glb',
  '/GodotBot.glb',
  '/GodotRifle.glb',
  '/GodotBotBox.glb',
  PICKAXE_MODEL,
  '/Python.glb',
  '/Web3dModel.glb',
];
let courseModelsRequested = false;
const requestCourseModels = () => {
  if (courseModelsRequested) return;
  courseModelsRequested = true;
  COURSE_MODELS.forEach((url) => useGLTF.preload(url));
};

const LAST = COURSES.length - 1;
// Starting guess for how far up an island reaches, used only for the frames
// before its meshes have been measured. The real bounds are taken from the
// geometry — see measureIslands.
const ISLAND_REVEAL_TOP = 4.8;
const ISLAND_REVEAL_BOTTOM = -0.12;
// Clearance around the measured bounds, so the frontier's noise cannot displace
// the sweep past the first or last of the geometry.
const REVEAL_MARGIN = 0.14;
// How close the camera has to be, in courses, before an island starts building.
// An island drops out of frame at about 0.61, so this also keeps the reveal from
// starting while the previous course still fills the shot.
const REVEAL_ARRIVAL_DISTANCE = 0.55;
// Frames to let pass before any island starts building. See the note in the
// materialiser's frame loop.
const REVEAL_WARM_FRAMES = 6;
// …and keep holding past that while frames are still visibly expensive, up to
// this many. See stillWarming.
const REVEAL_WARM_MAX_FRAMES = 30;

/**
 * Whether the opening animations should still be held back.
 *
 * A fixed frame count is a guess tuned on one machine, and the whole point of
 * this is to sit out work whose cost varies wildly by device. So the count is
 * only the floor: after it, this keeps holding while frames are still running
 * long, and gives up at a ceiling so a genuinely slow machine can never be held
 * forever.
 *
 * "Long" is measured against the quickest frame seen rather than a fixed
 * millisecond figure, because an absolute threshold reads every frame on a
 * 30fps device as a stall and would hold there until the ceiling every time.
 */
const stillWarming = (warm, delta) => {
  // Latched: this is a startup gate, not a jank filter. Left un-latched it
  // re-engages on any slow frame later on and pauses the animation mid-move,
  // which is its own kind of stutter — and damp already recovers from a dropped
  // frame on its own.
  if (warm.done) return false;

  warm.frames += 1;
  if (delta > 0) warm.floor = Math.min(warm.floor, delta);

  if (warm.frames >= REVEAL_WARM_MAX_FRAMES) { warm.done = true; return false; }
  if (warm.frames < REVEAL_WARM_FRAMES) return true;
  if (delta > warm.floor * 2) return true;

  warm.done = true;
  return false;
};

const newWarmState = () => ({ frames: 0, floor: Infinity, done: false });

// Flip to false to render the course world without post-processing. Useful for
// isolating whether a frame cost is the scene or the composer — but read the
// note by the EffectComposer first, because it changes how the world looks,
// not just how fast it draws.
const POST_PROCESSING = true;

/** Find the tagged island that owns a mesh without coupling the effect to any
    individual GLTF hierarchy. */
const islandIndexFor = (object) => {
  let cursor = object;
  while (cursor) {
    if (cursor.userData && cursor.userData.islandIndex !== undefined) {
      return cursor.userData.islandIndex;
    }
    cursor = cursor.parent;
  }
  return -1;
};

/**
 * Materialises the actual island geometry in two passes on the same mesh, so
 * skinning and animation can never drift apart: a projected wire lattice is
 * revealed across the real animated surface from the bottom up, then the model's
 * untouched full materials chase that lattice along the same path.
 *
 * The GLSL for both passes lives in ./hologramReveal — the two share one
 * frontier definition, and a pixel of disagreement between them tears the
 * hand-off open.
 */
const HologramMaterializer = ({ progressRef, visualProgressRef, reduced, sceneReadyRef, theme }) => {
  const { scene } = useThree();
  const cycles = useRef(COURSES.map(() => ({ wasChosen: false, elapsed: 0 })));
  const warm = useRef(newWarmState());
  // Per-mesh record of the two forms each one can take — see coats below.
  const coats = useRef([]);

  // One clock and one noise tile for every island: shared uniform objects mean
  // the whole world's lattice animates from a single write per frame.
  const revealTime = useMemo(() => ({ value: 0 }), []);
  // Shared and kept for the session — see getRevealNoiseTexture for why this
  // must not be built per mount.
  const revealNoise = useMemo(() => ({ value: getRevealNoiseTexture() }), []);

  const islandStates = useMemo(() => COURSES.map((course, index) => createRevealState({
    accent: course.accent,
    top: -index * SPACING + ISLAND_REVEAL_TOP,
    bottom: -index * SPACING + ISLAND_REVEAL_BOTTOM,
    revealed: reduced,
    time: revealTime,
    noise: revealNoise,
  })), [reduced, revealNoise, revealTime]);

  // Colours are uniforms, so a theme change is one cheap update per island —
  // no material rebuild and no shader compile in the middle of the wipe.
  useLayoutEffect(() => {
    islandStates.forEach((state, index) => {
      const course = COURSES[index];
      setRevealPalette(
        state,
        theme === 'light' ? course.accentInk : course.accent,
        theme === 'light'
      );
    });
  }, [islandStates, theme]);

  useLayoutEffect(() => {
    const assignments = [];
    const createdMaterials = [];
    const createdGeometries = [];
    const restores = [];
    const targets = [];
    const processed = new WeakSet();
    const materialUsers = new Map();
    coats.current = [];

    scene.traverse((object) => {
      if (object.isMesh) {
        const list = Array.isArray(object.material) ? object.material : [object.material];
        list.forEach((material) => {
          if (material) materialUsers.set(material, (materialUsers.get(material) || 0) + 1);
        });
      }
      const islandIndex = islandIndexFor(object);
      if (!object.isMesh || !object.geometry || islandIndex < 0) return;
      targets.push({ object, islandIndex });
    });

    /** Every material built here is owned by this effect and disposed with it. */
    const track = (material) => {
      createdMaterials.push(material);
      return material;
    };

    /**
     * Fit each island's sweep to the geometry it actually has.
     *
     * A fixed height cannot work here. The sweep is a constant-speed climb, so
     * any headroom above the tallest model is time the frontier spends in empty
     * space with the island already finished — and the islands are nowhere near
     * the same height. Measuring turns that dead tail into reveal on every one
     * of them, and it re-runs as late assets land, because a mesh that fits
     * itself a frame after Suspense resolves changes the answer.
     */
    const islandMeshes = COURSES.map(() => []);
    // Accumulated, never shrunk. Each pass unions in what the island measures
    // now, so the envelope can only grow as poses settle and late meshes land.
    // Growing is the safe direction: too large finishes the sweep a shade early,
    // too small leaves geometry cut away for as long as the course is on screen.
    const islandBounds = COURSES.map(() => new THREE.Box3());
    const meshBox = new THREE.Box3();

    const measureIslands = () => {
      islandMeshes.forEach((meshes, index) => {
        if (!meshes.length) return;
        const bounds = islandBounds[index];
        meshes.forEach((mesh) => {
          mesh.updateWorldMatrix(true, false);
          // A skinned mesh's geometry bounds describe its bind pose, which for a
          // rigged character is nothing like where it stands. computeBoundingBox
          // walks the bones instead — and it has to run every pass, not once:
          // the first passes land before the mixer has written a pose, and the
          // bot's bind pose is a hunch that measures 1.0 against the 3.7 it
          // reaches standing. Caching that first answer set the island's ceiling
          // below the bot's head, and everything above it stayed discarded.
          if (mesh.isSkinnedMesh) {
            mesh.computeBoundingBox();
            meshBox.copy(mesh.boundingBox);
          } else {
            if (!mesh.geometry.boundingBox) mesh.geometry.computeBoundingBox();
            meshBox.copy(mesh.geometry.boundingBox);
          }
          bounds.union(meshBox.applyMatrix4(mesh.matrixWorld));
        });
        if (bounds.isEmpty()) return;
        const state = islandStates[index];
        state.bottom.value = bounds.min.y - REVEAL_MARGIN;
        state.top.value = bounds.max.y + REVEAL_MARGIN;
      });
    };

    const processObject = ({ object, islandIndex }) => {
      if (processed.has(object)) return;
      processed.add(object);
      const state = islandStates[islandIndex];
      const originals = Array.isArray(object.material) ? object.material : [object.material];
      if (!originals.some(isRevealSurface)) return;

      // Patch where it stands if this mesh is the only user; clone if not.
      //
      // Cloning is what the islands' own animation was losing to. The ore block
      // pulses its emissive through a ref, and the mining debris fades itself by
      // writing straight to chip.material — both of which address the material
      // the component created, so once a clone was rendering in its place the
      // writes went nowhere and the chips never faded up from zero opacity.
      // A material authored in JSX has exactly one mesh; the ones that arrive
      // from a GLTF are shared (both the Godot and Minecraft decks are the same
      // platform file) and must be cloned or one island's sweep drives the
      // other's.
      const solidMaterials = originals.map((source) => {
        if (!isRevealSurface(source)) return source;
        if (materialUsers.get(source) === 1) {
          restores.push(applySolidReveal(source, state));
          return source;
        }
        return track(makeSolidMaterial(source, state));
      });
      const wireMaterial = track(makeLatticeMaterial(state));
      const materialCount = originals.length;
      const geometry = object.geometry.clone();
      const originalGroups = geometry.groups.length
        ? geometry.groups.map((group) => ({ ...group }))
        : [{
          start: 0,
          count: geometry.index ? geometry.index.count : geometry.attributes.position.count,
          materialIndex: 0,
        }];

      geometry.clearGroups();
      originalGroups.forEach(({ start, count, materialIndex = 0 }) => {
        geometry.addGroup(start, count, Math.min(materialIndex, materialCount - 1));
      });
      originalGroups.forEach(({ start, count, materialIndex = 0 }) => {
        geometry.addGroup(start, count, Math.min(materialIndex, materialCount - 1) + materialCount);
      });

      assignments.push({ object, material: object.material, geometry: object.geometry });
      createdGeometries.push(geometry);

      // Two forms, swapped by progress. While the island builds, the mesh
      // carries doubled groups so the lattice can draw over the same triangles.
      // Once it is solid the lattice has nothing left to say, so the mesh goes
      // back to its own geometry and a single material: that halves its draw
      // calls and drops a whole model's worth of vertex shading per frame for
      // as long as the course sits there — and it restores the plain
      // object.material the islands write to.
      coats.current.push({
        object,
        islandIndex,
        buildGeometry: geometry,
        restGeometry: object.geometry,
        buildMaterial: [...solidMaterials, ...originals.map(() => wireMaterial)],
        restMaterial: Array.isArray(object.material) ? solidMaterials : solidMaterials[0],
        building: null,
      });

      // Into the building coat immediately, before anything renders. The reveal
      // starts on the very first frame, so the warm-up below has to see exactly
      // the geometry and materials that frame will use — otherwise it warms the
      // originals and the reveal pays for its own upload mid-animation.
      const coat = coats.current[coats.current.length - 1];
      coat.building = true;
      coat.object.geometry = coat.buildGeometry;
      coat.object.material = coat.buildMaterial;

      islandMeshes[islandIndex].push(object);
    };

    targets.forEach(processObject);
    measureIslands();

    // The rifle is portalled into the animated hand after its bones are found,
    // and a few fitted assets also commit one frame after the Suspense boundary.
    // Catch those late meshes without watching or changing the page's load state.
    let refreshFrame = 0;
    let refreshId = 0;
    const refreshLateMeshes = () => {
      scene.traverse((object) => {
        if (processed.has(object)) return;
        const islandIndex = islandIndexFor(object);
        if (!object.isMesh || !object.geometry || islandIndex < 0) return;
        processObject({ object, islandIndex });
      });
      measureIslands();
      refreshFrame += 1;
      if (refreshFrame < 10) refreshId = requestAnimationFrame(refreshLateMeshes);
    };
    refreshId = requestAnimationFrame(refreshLateMeshes);

    return () => {
      cancelAnimationFrame(refreshId);
      assignments.forEach(({ object, material, geometry }) => {
        object.material = material;
        object.geometry = geometry;
      });
      restores.forEach((undo) => undo());
      createdMaterials.forEach((material) => material.dispose());
      createdGeometries.forEach((geometry) => geometry.dispose());
      coats.current = [];
    };
  }, [islandStates, reduced, scene]);

  useFrame(({ clock }, delta) => {
    if (reduced) {
      islandStates.forEach((state) => {
        state.progress.value = 1;
      });
      coats.current.forEach((coat) => {
        if (coat.building === false) return;
        coat.building = false;
        coat.object.geometry = coat.restGeometry;
        coat.object.material = coat.restMaterial;
      });
      return;
    }

    // Asset resolution and the opening-island GPU warm-up are separate jobs.
    // Do not let the one-second reveal run underneath the DOM loading cover.
    if (!sceneReadyRef.current) return;

    // Drives the lattice's scanlines and flicker. Shared, so it keeps running
    // whether or not any island is currently materialising.
    revealTime.value = clock.elapsedTime;

    // Let a couple of frames render before anything starts building.
    //
    // The post-processing chain compiles on first use — measured at four
    // fullscreen programs and ~29ms for bloom and its passes — and the warm-up
    // cannot pre-empt it, because the composer drives its own render outside
    // it. That cost cannot be removed, but it can be spent while there is
    // nothing to look at: at progress 0 every island is completely
    // un-revealed, so these frames show exactly what the visitor would see
    // anyway. Paid here it delays the sweep by ~33ms; paid a frame later it
    // lands as a stutter in the middle of it.
    if (stillWarming(warm.current, delta)) return;

    const d = Math.min(delta, 0.1);
    const position = visualProgressRef.current;
    const selected = progressRef.current;

    islandStates.forEach((state, index) => {
      const cycle = cycles.current[index];

      // Re-arm on the selection, not on the camera.
      //
      // The camera's position is damped, and it approaches the next course
      // asymptotically: it takes about two seconds for it to creep far enough
      // past the island behind it to count as having left. Anyone stepping
      // through courses quicker than that used to come back to an island that
      // was still solid and never saw it rebuild. The selection changes on the
      // press instead — and at that instant the camera has not moved yet, so
      // the island being selected is a whole course away and off screen, which
      // is what makes resetting it to nothing invisible.
      const chosen = selected === index;
      if (chosen && !cycle.wasChosen) {
        cycle.elapsed = 0;
        state.progress.value = 0;
      }
      cycle.wasChosen = chosen;

      // Hold at nothing until the flight is most of the way in. Run from the
      // top of the move, a 1.6s reveal is nearly spent by the time the island
      // is centred, so the arrival lands on something already assembled.
      // Once started it always runs out, so an island the camera only passes
      // through can never be left frozen half-built.
      const arriving = Math.abs(position - index) < REVEAL_ARRIVAL_DISTANCE;
      if ((arriving || cycle.elapsed > 0) && state.progress.value < 1) {
        cycle.elapsed += d;
        state.progress.value = clamp01(cycle.elapsed / REVEAL_SECONDS);
      }
    });

    // Swap each mesh between its building and resting form. Only meshes whose
    // island just crossed the boundary touch anything, so this costs a compare
    // per mesh in every other frame.
    coats.current.forEach((coat) => {
      const building = islandStates[coat.islandIndex].progress.value < 1;
      if (coat.building === building) return;
      coat.building = building;
      coat.object.geometry = building ? coat.buildGeometry : coat.restGeometry;
      coat.object.material = building ? coat.buildMaterial : coat.restMaterial;
    });
  });

  return null;
};

/**
 * Compile every island as soon as the shared asset boundary resolves.
 *
 * Compiling once with everything visible is not enough. three.js bakes the
 * *number* of lights into each material's program key, and the islands carry
 * their own model glows, so the count changes as courses cull in and out —
 * every new count is a fresh shader compile, landing mid-transition as a frame
 * spike. Warming the all-visible case only warms the maximum.
 *
 * So this also compiles the combinations the camera actually passes through:
 * each course alone, and each adjacent pair during a crossing. A short warm-up
 * here prevents visible shader compilation hitches during later camera moves.
 */
const CourseWarmup = ({ onReady, sceneReadyRef }) => {
  const { gl, scene, camera } = useThree();
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;

  useLayoutEffect(() => {
    sceneReadyRef.current = false;
    // Warm up after the first painted frame, not before it.
    //
    // The work below is a little over 400ms of blocking main thread — the
    // compile sweep plus a six-face probe render of every island. Run inside
    // the layout effect it lands between the scene being ready and anything
    // reaching the screen, so the whole of it is added to how long the page
    // appears to take to load. Nothing here is needed for the first frame; it
    // exists to keep later course flights smooth, and two frames of slack is
    // still far sooner than anyone can read the panel and press an arrow.
    let warmed = false;
    let warmFrame = 0;
    let readyId = 0;
    let stepTimer = 0;
    let finishWarmup = null;
    let warmId = requestAnimationFrame(function wait() {
      warmFrame += 1;
      if (warmFrame < 2) { warmId = requestAnimationFrame(wait); return; }
      warmId = 0;
      warmUpOnce();
    });
    // Backstop: a tab that never paints gets no animation frames at all, and the
    // warm-up would simply never happen.
    const warmTimer = setTimeout(warmUpOnce, 400);

    function warmUpOnce() {
      if (warmed) return;
      warmed = true;
      warmUp();
    }

    function warmUp() {
    // Culling off for the duration, so islands off screen still issue draws and
    // get their buffers built. Restored by the last step.
    //
    // Nothing here touches `visible` on anything but the island wrappers, and
    // that is deliberate. There used to be a matching pass that recorded every
    // already-hidden object and re-hid it at the end. That was safe while the
    // warm-up was one synchronous block, but these steps now span some thirty
    // frames — long enough for React to have committed visible={true} on the
    // bot once it finished posing, and the restore wrote false straight back
    // over it. React had no reason to re-render, so the bot stayed hidden for
    // good. Never hold a snapshot of React-owned state across a frame boundary.
    const culled = [];
    scene.traverse((object) => {
      if (object.frustumCulled) {
        culled.push(object);
        object.frustumCulled = false;
      }
    });

    // Islands are the direct children tagged during render; find them by the
    // marker their wrapper sets.
    const islands = [];
    scene.traverse((object) => {
      if (object.userData && object.userData.islandIndex !== undefined) islands.push(object);
    });

    const setOnly = (indices) => {
      islands.forEach((island) => {
        island.visible = indices.includes(island.userData.islandIndex);
      });
    };

    // Warm one step per frame instead of all of it in one go.
    //
    // The total is what it is: compiling every visibility combination, then
    // drawing every material once so the driver uploads its textures and builds
    // its vertex buffers. Done in a single task that is a ~400ms stall. Spread a
    // step per frame it is a series of short ones, and the steps are ordered so
    // the island actually on screen is ready first and the pairs the camera only
    // needs mid-flight come last.
    //
    // Visibility does not need restoring between steps: Island sets it from the
    // camera every frame, so whatever a step leaves behind is corrected before
    // anything is rendered.
    // The island already on screen is warmed here and now, synchronously.
    //
    // Deferring this was a mistake worth spelling out. The reveal begins on the
    // very first frame, so anything put off until "after first paint" is warmed
    // only after it has been drawn — which moved a ~90ms stall out of the load
    // and into the middle of the materialise animation, where it is far more
    // obvious. Measured: frame 0 of the reveal was uploading 1.8MB of vertex
    // buffers, 17 textures and linking 12 programs while the sweep was running.
    //
    // This cost is unavoidable; only its timing is ours to choose, and before
    // anything is visible beats during the one animation on the page.
    const warmTarget = new THREE.WebGLRenderTarget(32, 32);
    const drawIsland = (i) => {
      // Warm what is currently hidden, too.
      //
      // gl.compile walks the scene with traverseVisible and gl.render skips
      // invisible objects, so anything hidden right now is passed over entirely
      // and pays for its shaders and its upload later — whenever it first
      // appears. The bot is exactly that case: it stays invisible until the
      // mixer has written a real pose, so it was being skipped here and then
      // uploading 1.2MB and linking three programs on the frame it appeared,
      // for a 58ms stall in the middle of the reveal.
      //
      // Captured and restored inside this one synchronous block, deliberately.
      // A visibility snapshot must never outlive the frame it was taken in:
      // held across frames, this same pattern once wrote visible=false over the
      // value React had committed and left the bot hidden permanently.
      const hidden = [];
      scene.traverse((object) => {
        if (!object.visible) { hidden.push(object); object.visible = true; }
      });

      setOnly([i]);
      gl.compile(scene, camera);
      const previous = gl.getRenderTarget();
      gl.setRenderTarget(warmTarget);
      gl.render(scene, camera);
      gl.setRenderTarget(previous);

      hidden.forEach((object) => { object.visible = false; });
    };
    drawIsland(0);
    // At this point the opening island has linked its shaders, uploaded its
    // textures and built its buffers. Let the DOM loading cover leave on the
    // next paint, just before the reveal starts moving.
    sceneReadyRef.current = true;
    readyId = requestAnimationFrame(() => onReadyRef.current?.());

    // Everything else can wait for later frames.
    const steps = [];
    for (let i = 1; i <= LAST; i++) steps.push(() => { setOnly([i]); gl.compile(scene, camera); });
    for (let i = 0; i < LAST; i++) steps.push(() => { setOnly([i, i + 1]); gl.compile(scene, camera); });

    // gl.compile builds the programs but does not upload textures, and the
    // upload is the expensive half: the first draw that uses a texture blocks
    // while the driver pushes it to the GPU. Measured, one island's worth of
    // that in a single frame is over half a second.
    //
    // initTexture does the upload directly, without a draw, so it can be done
    // one texture at a time. That is the whole reason this is chunkable at all.
    // It also replaces a CubeCamera that used to draw the scene six times over,
    // once per cube face, each face warming exactly what the last one had —
    // nothing here feeds an environment probe, the lighting comes from
    // <Environment> and its own PMREM.
    const textures = new Set();
    const SLOTS = ['map', 'normalMap', 'roughnessMap', 'metalnessMap', 'emissiveMap', 'aoMap', 'alphaMap'];
    scene.traverse((object) => {
      if (!object.isMesh) return;
      const list = Array.isArray(object.material) ? object.material : [object.material];
      list.forEach((material) => {
        if (!material) return;
        SLOTS.forEach((slot) => { if (material[slot]) textures.add(material[slot]); });
      });
    });
    textures.forEach((texture) => steps.push(() => gl.initTexture(texture)));

    // Vertex buffers are created on an island's first real draw, and without
    // this every first flight to a course dropped a frame or three doing it
    // (measured 42-67ms). One island per step, and deliberately after the
    // uploads above so each of these pays only for buffers, not textures.
    for (let i = 1; i <= LAST; i++) steps.push(() => drawIsland(i));

    let finished = false;
    finishWarmup = () => {
      if (finished) return;
      finished = true;
      warmTarget.dispose();
      islands.forEach((island) => { island.visible = true; });
      culled.forEach((object) => { object.frustumCulled = true; });
    };
    steps.push(finishWarmup);

    let step = 0;
    const runStep = () => {
      steps[step]();
      step += 1;
      if (step < steps.length) warmId = requestAnimationFrame(runStep);
      else warmId = 0;
    };

    // Only island zero is needed for the opening shot. The remaining compile
    // combinations and texture uploads used to run one per animation frame
    // while that island materialised, producing the exact first-open hitch the
    // warm-up was meant to avoid. Start them after the one-second reveal.
    stepTimer = setTimeout(runStep, (REVEAL_SECONDS + 0.25) * 1000);

    }

    return () => {
      if (warmId) cancelAnimationFrame(warmId);
      if (readyId) cancelAnimationFrame(readyId);
      clearTimeout(stepTimer);
      clearTimeout(warmTimer);
      finishWarmup?.();
      sceneReadyRef.current = false;
    };
  }, [camera, gl, scene, sceneReadyRef]);

  return null;
};

/** Mobile stacks the panel under the scene, so the island is parked this far
    up the viewport (as a fraction of its height). */
/* ── Camera ───────────────────────────────────────────────────────────────
   `progressRef.current` is a float: 0 = first course centred, 1 = second, …
   The camera eases toward it and arcs outward mid-flight, which sells the
   travel between islands without relying on page scrolling.              */
const CameraRig = ({ progressRef, visualProgressRef, reduced, isMobile, sceneReadyRef }) => {
  const smoothed = useRef(0);
  const look = useRef(new THREE.Vector3());
  const entrance = useRef(0);
  const warm = useRef(newWarmState());

  // No pointer parallax and no per-island spin: the camera travels, the
  // islands hold still.
  useFrame(({ camera, size }, delta) => {
    const target = progressRef.current;
    smoothed.current = reduced
      ? target
      : THREE.MathUtils.damp(smoothed.current, target, 3.4, Math.min(delta, 0.1));
    visualProgressRef.current = smoothed.current;

    // Hold the fly-in for the same frames the reveal waits out.
    //
    // The camera is still placed every frame — it simply keeps its opening pose
    // instead of starting to move while the post-processing chain is compiling.
    // That compile is ~33ms on the first frame, and a move that begins on it
    // stutters exactly where the eye is drawn to it: at the start.
    const warming = !sceneReadyRef.current || stillWarming(warm.current, delta);

    if (reduced) {
      entrance.current = 1;
    } else if (!warming) {
      entrance.current = THREE.MathUtils.damp(entrance.current, 1, 1.5, Math.min(delta, 0.1));
    }

    const p = smoothed.current;
    // 0 at an island, 1 exactly between two islands.
    const between = reduced ? 0 : Math.sin(Math.abs(p - Math.round(p)) * Math.PI);

    // The overlay panel covers the leading side of the screen on desktop, so
    // aim slightly past the island to push it into the free half.
    const aimOffset = isMobile ? 0 : 2.4;
    // Courses are stacked below one another. Moving to the next index lowers
    // the camera through the world, so the incoming island rises naturally
    // from the bottom instead of sliding in from the side.
    const courseY = -p * SPACING;

    const entranceDistance = (1 - entrance.current) * (isMobile ? 8.5 : 10.5);
    const entranceHeight = (1 - entrance.current) * (isMobile ? 2.2 : 2.8);

    camera.position.set(
      aimOffset * 0.55,
      courseY + (isMobile ? 3.4 : 3.0) + entranceHeight,
      (isMobile ? 12.4 : 9.6) + between * 3.6 + entranceDistance
    );

    look.current.set(
      aimOffset,
      courseY + (isMobile ? 1.9 : 1.7),
      0
    );
    camera.lookAt(look.current);
    const fov = (isMobile ? 52 : 45) + between * 5 + (1 - entrance.current) * 8;
    if (Math.abs(camera.fov - fov) > 0.01) {
      camera.fov = fov;
      camera.updateProjectionMatrix();
    }

    // Slide the frustum down so the island sits in the top of a phone screen
    // and the panel gets the bottom half. Panning the projection rather than
    // moving the camera keeps the shot at the angle it was composed at. Short
    // phones need more lift, while tall phones need less so the model is not
    // pushed unnecessarily close to the top edge.
    if (isMobile) {
      const portraitRatio = size.height / Math.max(size.width, 1);
      const mobileLift = THREE.MathUtils.clamp(
        0.24 - (portraitRatio - 1.7) * 0.13,
        0.15,
        0.24
      );
      camera.setViewOffset(
        size.width, size.height,
        0, size.height * mobileLift,
        size.width, size.height
      );
    } else if (camera.view && camera.view.enabled) {
      camera.clearViewOffset();
    }
  });

  return null;
};

/* ── Island shell ─────────────────────────────────────────────────────────
   Keeps only the current transition pair mounted for rendering. The island
   itself stays still so the two materialisation passes remain easy to read. */
const Island = ({ index, progressRef, children }) => {
  const group = useRef();

  useFrame(() => {
    const g = group.current;
    if (!g) return;
    const p = progressRef.current;
    const distance = Math.abs(p - index);

    // A transition only needs the outgoing and incoming islands. Keeping the
    // island on the far side visible made three full scenes overlap around a
    // course centre and caused a noticeable frame-time spike on phones.
    g.visible = distance < 0.999;
  });

  return (
    <group position={[0, -index * SPACING, 0]}>
      {/* Tagged so the warm-up can drive island visibility directly and compile
          each light-count combination the camera will pass through. */}
      <group ref={group} userData={{ islandIndex: index }}>
        {children}
      </group>
    </group>
  );
};

/**
 * One accent rig for the whole world, travelling with the camera.
 *
 * These lights used to live inside each island, which meant twelve of them and
 * — worse — a light count that changed every time an island culled. three.js
 * bakes the number of lights into every material's program key, so each new
 * count compiled a fresh set of shaders mid-scroll. That is a frame spike you
 * cannot damp away, and it landed exactly during course transitions.
 *
 * Three lights, always present, following the active course and cross-fading
 * to its accent: one permutation, compiled once, and a quarter of the
 * per-fragment light maths.
 */
const TravellingLights = ({ visualProgressRef }) => {
  const group = useRef();
  const accentLight = useRef();

  const accents = useMemo(() => COURSES.map((c) => new THREE.Color(c.accent)), []);

  useFrame(() => {
    const p = clamp01(visualProgressRef.current / Math.max(LAST, 1)) * Math.max(LAST, 1);
    if (group.current) group.current.position.y = -visualProgressRef.current * SPACING;
    if (accentLight.current) {
      const i = Math.min(Math.floor(p), LAST);
      const next = accents[Math.min(i + 1, LAST)];
      accentLight.current.color.copy(accents[i]).lerp(next, p - i);
    }
  });

  return (
    <group ref={group}>
      {/* Reach is wider than the per-island version was: during a transition
          this rig sits between two courses and has to light both. */}
      <pointLight ref={accentLight} position={[2.6, 4.2, 3.4]} intensity={2.8} distance={26} decay={2} />
      <pointLight position={[-3.4, 2.4, -3.2]} intensity={1.7} distance={24} decay={2} color="#7FA8FF" />
      <spotLight
        position={[0, 8, 2]}
        angle={0.7}
        penumbra={0.9}
        intensity={2.4}
        distance={34}
        decay={2}
        color="#FFFFFF"
      />
    </group>
  );
};

/* ── Scene palette ────────────────────────────────────────────────────────
   The rig, the fog and the bloom pass were all tuned against a near-black
   page, and each of them misbehaves differently on white, so every theme
   carries its own numbers.                                                */
const SCENE = {
  dark: {
    fog: ['#070B16', 14, 48],
    ambient: 0.45,
    hemi: ['#9DC3E6', '#0A1020', 0.55],
    key: 1.15,
    rim: 0,
    bloom: { intensity: 0.75, threshold: 0.55, smoothing: 0.32 },
    vignette: { offset: 0.32, darkness: 0.5 },
    sparkle: '#9DC3E6',
  },
  light: {
    // Match the blue middle distance in SceneBackdrop so geometry fades into
    // an actual daytime sky rather than a flat near-white wash.
    fog: ['#B3DBEE', 18, 54],
    ambient: 0.5,
    hemi: ['#F2FAFF', '#AFC9DC', 0.65],
    key: 1.5,
    // Back light to carve the silhouette away from a bright background —
    // on white there is no dark surround doing that job for free.
    rim: 1.1,
    // A white page is brighter than a 0.55 threshold, so the *background*
    // blooms and hazes everything. Only genuinely emissive bits should glow.
    bloom: { intensity: 0.22, threshold: 0.95, smoothing: 0.1 },
    vignette: { offset: 0.4, darkness: 0.3 },
    sparkle: '#3D82B5',
  },
};

/**
 * Small, texture-free motes for the course world.
 *
 * Drei's stock Sparkles shader lets its analytic alpha falloff reach (and at
 * the square's corners pass) zero without clipping the fragment. That is
 * normally invisible on navy, but the post-processing chain can turn those
 * almost-transparent samples into a gray fringe on a bright backdrop. This
 * mask has a finite feather and discards its transparent edge explicitly, so
 * there is no dark RGB or invisible fringe for the composer to resample.
 */
const CourseParticles = ({ count, scale, position, size, speed, opacity, color, light }) => {
  const material = useRef();

  const attributes = useMemo(() => {
    const positions = new Float32Array(count * 3);
    const sizes = new Float32Array(count);
    const phases = new Float32Array(count);
    const speeds = new Float32Array(count);

    for (let i = 0; i < count; i += 1) {
      positions[i * 3] = (Math.random() - 0.5) * scale[0];
      positions[i * 3 + 1] = (Math.random() - 0.5) * scale[1];
      positions[i * 3 + 2] = (Math.random() - 0.5) * scale[2];
      sizes[i] = size * (0.72 + Math.random() * 0.5);
      phases[i] = Math.random() * Math.PI * 2;
      speeds[i] = speed * (0.72 + Math.random() * 0.56);
    }

    return { positions, sizes, phases, speeds };
  }, [count, scale, size, speed]);

  const uniforms = useMemo(() => ({
    uTime: { value: 0 },
    uColor: { value: new THREE.Color(color) },
    uOpacity: { value: opacity },
  }), [color, opacity]);

  useFrame(({ clock }) => {
    if (material.current) material.current.uniforms.uTime.value = clock.elapsedTime;
  });

  return (
    <points position={position}>
      <bufferGeometry>
        <bufferAttribute attach="attributes-position" array={attributes.positions} count={count} itemSize={3} />
        <bufferAttribute attach="attributes-aSize" array={attributes.sizes} count={count} itemSize={1} />
        <bufferAttribute attach="attributes-aPhase" array={attributes.phases} count={count} itemSize={1} />
        <bufferAttribute attach="attributes-aSpeed" array={attributes.speeds} count={count} itemSize={1} />
      </bufferGeometry>
      <shaderMaterial
        ref={material}
        uniforms={uniforms}
        transparent
        depthWrite={false}
        premultipliedAlpha={false}
        blending={light ? THREE.NormalBlending : THREE.AdditiveBlending}
        vertexShader={`
          uniform float uTime;
          attribute float aSize;
          attribute float aPhase;
          attribute float aSpeed;

          void main() {
            vec3 animated = position;
            float motion = uTime * aSpeed + aPhase;
            animated.y += sin(motion) * 0.16;
            animated.x += cos(motion * 0.73) * 0.08;

            vec4 viewPosition = modelViewMatrix * vec4(animated, 1.0);
            gl_Position = projectionMatrix * viewPosition;
            gl_PointSize = clamp(aSize * 24.0 / max(1.0, -viewPosition.z), 1.25, 6.5);
          }
        `}
        fragmentShader={`
          uniform vec3 uColor;
          uniform float uOpacity;

          void main() {
            float distanceToCenter = distance(gl_PointCoord, vec2(0.5));
            float mask = 1.0 - smoothstep(0.16, 0.5, distanceToCenter);
            mask *= uOpacity;
            if (mask < 0.025) discard;
            gl_FragColor = vec4(uColor, mask);
          }
        `}
      />
    </points>
  );
};

const Scene = ({ progressRef, reduced, isMobile, onReady }) => {
  const theme = useTheme();
  const visualProgressRef = useRef(0);
  const sceneReadyRef = useRef(false);
  const cfg = SCENE[theme] || SCENE.dark;
  const backdrop = useSceneBackdrop(theme, 'courses');
  const sparkleBounds = useMemo(
    () => [26, SPACING * LAST + 26, 26],
    []
  );

  return (
    <>
      {/* three.js can't parse var(), so the scene carries its own palette and
          swaps it on the theme-change event. */}
      <primitive attach="background" object={backdrop} />
      <fog attach="fog" args={cfg.fog} />

      <ambientLight intensity={cfg.ambient} />
      <hemisphereLight args={cfg.hemi} />
      <directionalLight position={[6, 12, 8]} intensity={cfg.key} />
      {/* Always mounted, even at zero intensity in dark: a light appearing or
          disappearing changes the program key and recompiles every material. */}
      <directionalLight position={[-7, 5, -9]} intensity={cfg.rim} color="#A8C6E8" />

      <TravellingLights visualProgressRef={visualProgressRef} />

      <Suspense fallback={null}>
        <Environment files="/environment.hdr" background={false} />

        {COURSES.map((course, i) => (
          <Island
            key={course.id}
            index={i}
            progressRef={visualProgressRef}
          >
            {course.scene.kind === 'godot' && (
              <GodotIsland accent={course.accent} accentInk={course.accentInk} reduced={reduced} />
            )}
            {course.scene.kind === 'python' && (
              <PythonIsland accent={course.accent} accentInk={course.accentInk} reduced={reduced} />
            )}
            {course.scene.kind === 'minecraft' && (
              <MinecraftIsland accent={course.accent} accentInk={course.accentInk} reduced={reduced} />
            )}
            {course.scene.kind === 'web3d' && (
              <Web3DIsland accent={course.accent} accentInk={course.accentInk} reduced={reduced} />
            )}
            {course.scene.kind === 'placeholder' && (
              <PlaceholderIsland
                variant={course.scene.variant}
                accent={course.accent}
                accentInk={course.accentInk}
                reduced={reduced}
              />
            )}
          </Island>
        ))}

        <HologramMaterializer
          progressRef={progressRef}
          visualProgressRef={visualProgressRef}
          reduced={reduced}
          sceneReadyRef={sceneReadyRef}
          theme={theme}
        />

        {/* This mounts only after every island asset has resolved. Compile the
            material variants once so later camera flights remain smooth. */}
        <CourseWarmup onReady={onReady} sceneReadyRef={sceneReadyRef} />
      </Suspense>

      {!reduced && (
        <CourseParticles
          count={isMobile ? 80 : 220}
          scale={sparkleBounds}
          position={[0, 4 - (SPACING * LAST) / 2, 0]}
          size={2.4}
          speed={0.25}
          opacity={theme === 'light' ? 0.48 : 0.5}
          color={cfg.sparkle}
          light={theme === 'light'}
        />
      )}

      <CameraRig
        progressRef={progressRef}
        visualProgressRef={visualProgressRef}
        reduced={reduced}
        isMobile={isMobile}
        sceneReadyRef={sceneReadyRef}
      />

      {/* Bloom handles the emissive glow. On mobile, SMAA restores clean model
          edges without bringing back the more expensive multisampled buffer.

          Turning this off is not a like-for-like comparison. The reveal's glow
          colours are deliberately mixed past 1.0 so they cross the bloom
          threshold, and the models' own emissives — crate panels, halo rings —
          are lit the same way. Without the composer all of that renders flat.
          The canvas also asks for antialias:false below, because the composer
          supplies its own multisampling, so edges come back jagged too. */}
      {POST_PROCESSING && (
        <EffectComposer disableNormalPass multisampling={isMobile ? 0 : 2}>
          <Bloom
            intensity={cfg.bloom.intensity * (isMobile ? 0.6 : 1)}
            luminanceThreshold={cfg.bloom.threshold}
            luminanceSmoothing={cfg.bloom.smoothing}
            mipmapBlur={!isMobile}
          />
          {isMobile && <SMAA />}
          {/* Vignette temporarily disabled for visual comparison. */}
        </EffectComposer>
      )}
    </>
  );
};

const CourseWorld = ({ progressRef, reduced, isMobile, onReady }) => {
  requestCourseModels();

  return (
    <Canvas
      className="webgl-interaction-surface"
      // A 1.5 cap undersamples the common 2-3x phone screen and makes both the
      // model silhouette and the reveal's one-pixel lattice look blocky.
      dpr={[1, 2]}
      // The course viewport is stable and only its page position changes while
      // visiting the footer. Do not ask r3f to remeasure on every scroll event.
      resize={{ scroll: false, debounce: { scroll: 50, resize: 0 } }}
      // The EffectComposer renders to its own multisampled target, so the
      // canvas's own MSAA buffer is allocated and never sampled.
      gl={{ antialias: false, powerPreference: 'high-performance' }}
      camera={{ fov: isMobile ? 52 : 45, near: 0.1, far: 220, position: [0, 3, 10] }}
      style={{ width: '100%', height: '100%' }}
    >
      <Scene
        progressRef={progressRef}
        reduced={reduced}
        isMobile={isMobile}
        onReady={onReady}
      />
    </Canvas>
  );
};

export default CourseWorld;
